import { useCallback, useEffect, useMemo, useState } from 'react'
import DataGrid, { Column, RenderCellProps } from 'react-data-grid'
import { useTranslation } from 'react-i18next'
import { NavLink, useHistory, useParams } from 'react-router-dom'
import { formatCurrencyToSwissLocale, printDate } from '../../helpers/Utils'
import { isDatabaseReport, useGetReportQuery } from '../../redux/api/reportsApi'
import { Reports } from '../../utils/reports'
import DataSecurityPopup from '../ContractDetails/Popups/DataSecurityPopup'
import Alert from '../common/Alert/Alert'
import './ReportsComponent.scss'
import { DatabaseReportViewModel } from '@ail/reports-api'
import { Loader } from '../common/Loader/Loader'

type DatabaseReportRow = Partial<Record<string, string | number>>

export function DatabaseReportView() {
  const [attempt, setAttempt] = useState<{ report?: DatabaseReportViewModel; number: number }>({ number: 0 })
  const [pendingExportConfirm, setPendingExportConfirm] = useState<{ onConfirm: () => void }>()
  const params = useParams<{ reportId: string; force?: string }>()
  const force = params.force === 'true'
  const [canPoll, setCanPoll] = useState(!force)
  const history = useHistory()
  const { t } = useTranslation('common')

  const stopPoll = attempt.number > 4 || (!!attempt.report?.data && !attempt.report.running)

  const forcedReport = useGetReportQuery(
    { reportId: params.reportId, force: true },
    { skip: !force, refetchOnMountOrArgChange: true }
  )

  useEffect(() => {
    if (!force || !forcedReport.fulfilledTimeStamp) return
    const timer = setTimeout(() => setCanPoll(true), 7000)
    return () => clearTimeout(timer)
  }, [force, forcedReport.fulfilledTimeStamp])

  const report = useGetReportQuery(
    { reportId: params.reportId, force: false },
    {
      pollingInterval: !stopPoll ? 15000 : undefined,
      skip: force && !canPoll,
      refetchOnMountOrArgChange: force,
    }
  )

  const height = window.innerHeight > 220 ? window.innerHeight - 220 : window.innerHeight

  useEffect(() => {
    let result: DatabaseReportViewModel | undefined = undefined
    if (report.currentData && isDatabaseReport(report.currentData)) result = report.currentData
    if (!result && forcedReport.currentData && isDatabaseReport(forcedReport.currentData))
      result = forcedReport.currentData
    setAttempt((current) => (result !== current.report ? { report: result, number: current.number + 1 } : current))
  }, [forcedReport.currentData, report.currentData])

  function printDateIfValid(dateArg: string | number | undefined, withTime?: boolean) {
    if (!dateArg) return null
    const receivedDate = new Date(dateArg)

    const currentYear = new Date().getFullYear()

    const minValidYear = currentYear - 50
    const maxValidYear = currentYear + 50

    const year = receivedDate.getFullYear()
    if (year < minValidYear || year > maxValidYear) return null

    return printDate(receivedDate, withTime)
  }

  const renderDateCell = useCallback((data: RenderCellProps<DatabaseReportRow>) => {
    return printDateIfValid(data.row?.[data.column.key])
  }, [])

  const renderCurrencyCell = useCallback((data: RenderCellProps<DatabaseReportRow>) => {
    const val = data.row[data.column.key]
    const numVal = val != null ? Number(val) : undefined
    return numVal == null || isNaN(numVal) ? undefined : (
      <span className="currencyCell">{formatCurrencyToSwissLocale(numVal)}</span>
    )
  }, [])

  const columns = useMemo(() => {
    if (!attempt.report) return []
    const result: Column<DatabaseReportRow>[] = attempt.report.fields.map((f) => {
      return {
        key: f.name,
        name: f.name,
        renderCell: f.settings?.dateFormat ? renderDateCell : f.settings?.isCurrency ? renderCurrencyCell : undefined,
      }
    })
    return result
  }, [attempt.report, renderCurrencyCell, renderDateCell])

  const rows = useMemo(() => {
    if (!attempt.report || !attempt.report.data) return []
    return attempt.report.data as unknown as DatabaseReportRow[]
  }, [attempt.report])

  const exportWYSIWYG = () => {
    if (!attempt.report) return
    Reports.exportDatabaseReport(attempt.report)
  }

  return (
    <div className="reports-component">
      <Loader blocking={!stopPoll} style={{ overflow: 'hidden' }}>
        {attempt.report && columns.length > 0 && (
          <>
            <ul className="breadcrumbs">
              <li>
                <NavLink to="/reports" title={t('REPORTS')}>
                  {t('REPORTS')}
                </NavLink>
              </li>
              <li>
                <span>{attempt.report.name}</span>
              </li>
              <li>
                {attempt.report.failed && stopPoll && (
                  <span className="failed-report-message">
                    <i className="icon-warning" /> {t('FAILED_REPORT_MESSAGE')}
                  </span>
                )}
              </li>
            </ul>
            <div className="contractFilters">
              <div className="leftFilters">
                <a onClick={() => setPendingExportConfirm({ onConfirm: exportWYSIWYG })} className="exportLink">
                  <i className="icon-excel" /> <span>{t('EXPORT_CONTRACT_TO_EXCEL')}</span>
                </a>
              </div>
            </div>
            <div className="report-grid-wrap" style={{ height: height + 'px' }}>
              <DataGrid columns={columns} rows={rows} />
            </div>
          </>
        )}
        <Alert
          visible={!attempt.report?.data && stopPoll}
          onClose={() => {
            history.goBack()
          }}
          title={t('WARNING')}
          message={`${t('REPORT_TAKING_LONGER')}`}
        />
        <DataSecurityPopup
          onConfirm={() => {
            pendingExportConfirm?.onConfirm()
            setPendingExportConfirm(undefined)
          }}
          onClose={() => setPendingExportConfirm(undefined)}
          visible={!!pendingExportConfirm}
        />
      </Loader>
    </div>
  )
}
